package dev.terrakok.mapitan

import androidx.compose.ui.graphics.Color
import androidx.compose.ui.input.key.*
import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import dev.terrakok.mapitan.domain.*
import dev.terrakok.mapitan.domain.geojson.MapKeys
import dev.terrakok.mapitan.domain.geojson.AreaDraft
import dev.terrakok.mapitan.domain.geojson.AreaPointDrag
import dev.terrakok.mapitan.domain.geojson.LabelDrag
import dev.terrakok.mapitan.domain.geojson.PinDrag
import dev.terrakok.mapitan.domain.geojson.TrackDraft
import dev.terrakok.mapitan.domain.geojson.TrackPointDrag
import dev.terrakok.mapitan.domain.geojson.insertIndexFor
import dev.terrakok.mapitan.ui.components.Mode
import dev.terrakok.mapitan.domain.utils.generateId
import kotlinx.coroutines.ExperimentalCoroutinesApi
import kotlinx.coroutines.flow.*
import org.maplibre.spatialk.geojson.Point
import org.maplibre.spatialk.geojson.Position

@OptIn(ExperimentalCoroutinesApi::class)
class ProjectViewModel(
    private val project: Project = Project(),
) : ViewModel() {
    companion object {
        val DefaultColor = Color(0xFFC83232)
        val DefaultLineThickness = 3
        val DefaultLineStyle = LineStyle.Solid
    }

    val mode: StateFlow<Mode>
        field = MutableStateFlow(Mode.Cursor)

    private val selectedObjectId = MutableStateFlow<String?>(null)

    val selectedObject: StateFlow<DocumentObject?> = selectedObjectId.combine(project.document) { id, doc ->
        id?.let { doc.findById(id) }
    }.stateIn(viewModelScope, SharingStarted.Eagerly, null)

    private val customColor = MutableStateFlow(DefaultColor)
    private val customLineThickness = MutableStateFlow(DefaultLineThickness)
    private val customLineStyle = MutableStateFlow(DefaultLineStyle)
    private val customLabelText = MutableStateFlow("")

    val selectedColor: Flow<Color> = selectedObject.flatMapLatest { obj ->
        when (obj) {
            is Pin -> flowOf(obj.color)
            is Track -> flowOf(obj.color)
            is Area -> flowOf(obj.color)
            is Label -> flowOf(obj.color)
            null -> customColor
        }
    }

    val selectedLineThickness: Flow<Int> = selectedObject.flatMapLatest { obj ->
        when (obj) {
            is Track -> flowOf(obj.lineThickness)
            is Area -> flowOf(obj.lineThickness)
            else -> customLineThickness
        }
    }

    val selectedLineStyle: Flow<LineStyle> = selectedObject.flatMapLatest { obj ->
        when (obj) {
            is Track -> flowOf(obj.lineStyle)
            is Area -> flowOf(obj.lineStyle)
            else -> customLineStyle
        }
    }

    val labelText: Flow<String> = selectedObject.flatMapLatest { obj ->
        if (obj is Label) flowOf(obj.text) else customLabelText
    }

    val mapToolIsLocked: StateFlow<Boolean>
        field = MutableStateFlow(false)

    val draggingPin: StateFlow<PinDrag?>
        field = MutableStateFlow<PinDrag?>(null)

    val draggingLabel: StateFlow<LabelDrag?>
        field = MutableStateFlow<LabelDrag?>(null)

    val trackDraft: StateFlow<TrackDraft?>
        field = MutableStateFlow<TrackDraft?>(null)

    val draggingTrackPoint: StateFlow<TrackPointDrag?>
        field = MutableStateFlow<TrackPointDrag?>(null)

    val areaDraft: StateFlow<AreaDraft?>
        field = MutableStateFlow<AreaDraft?>(null)

    val draggingAreaPoint: StateFlow<AreaPointDrag?>
        field = MutableStateFlow<AreaPointDrag?>(null)

    val document = project.document
    val canUndo = project.canUndo
    val canRedo = project.canRedo

    fun undo() {
        if (canUndo.value) {
            resetState()
            project.undo()
        }
    }

    fun redo() {
        if (canRedo.value) {
            resetState()
            project.redo()
        }
    }

    fun clickOnMap(
        position: Position,
        objectIds: List<String>,
        areaBorderIds: List<String>,
    ) {
        when (mode.value) {
            Mode.Cursor -> {
                when (val selected = selectedObject.value) {
                    is Track -> if (objectIds.contains(selected.id)) {
                        println("Add point to track")
                        addTrackPoint(selected, position)
                    } else {
                        selectedObjectId.value = objectIds.firstOrNull()
                    }

                    is Area -> if (areaBorderIds.contains(selected.id)) {
                        println("Add point to area")
                        addAreaPoint(selected, position)
                    } else if (!objectIds.contains(selected.id)) {
                        selectedObjectId.value = objectIds.firstOrNull()
                    }

                    else -> {
                        println("No object selected")
                        selectedObjectId.value = objectIds.firstOrNull()
                    }
                }
            }

            Mode.Pin -> {
                addNewPin(position)
            }

            Mode.Track -> {
                newPointInTrack(position, objectIds)
            }

            Mode.Area -> {
                newPointInArea(position, objectIds)
            }

            Mode.Text -> {
                addNewLabel(position)
            }
        }
    }

    private fun addNewPin(position: Position) {
        val newId = generateId()
        val pin = Pin(
            id = newId,
            point = Point(position),
            color = customColor.value,
        )
        project.addPin(pin)
        if (!mapToolIsLocked.value) {
            mode.value = Mode.Cursor
            selectedObjectId.value = newId
        }
    }

    private fun addNewLabel(position: Position) {
        val newId = generateId()
        project.addLabel(
            Label(
                id = newId,
                point = Point(position),
                color = customColor.value,
                text = customLabelText.value,
            )
        )
        if (!mapToolIsLocked.value) {
            mode.value = Mode.Cursor
            selectedObjectId.value = newId
        }
    }

    private fun newPointInTrack(position: Position, objectIds: List<String>) {
        val draft = trackDraft.value
        if (draft != null && objectIds.contains(MapKeys.DRAFT_TRACK_ID)) {
            finishDraftTrack()
        } else {
            val newPoint = TrackPoint(generateId(), Point(position))
            trackDraft.value = draft?.copy(points = draft.points + newPoint)
                ?: TrackDraft(
                    points = listOf(newPoint),
                    color = customColor.value,
                    lineThickness = customLineThickness.value,
                    lineStyle = customLineStyle.value,
                )
        }
    }

    private fun finishDraftTrack() {
        val draft = trackDraft.value ?: return
        trackDraft.value = null
        if (draft.points.size < 2) return
        val newId = generateId()
        project.addTrack(
            Track(
                id = newId,
                points = draft.points,
                color = draft.color,
                lineThickness = draft.lineThickness,
                lineStyle = draft.lineStyle,
            )
        )
        if (!mapToolIsLocked.value) {
            mode.value = Mode.Cursor
            selectedObjectId.value = newId
        }
    }

    private fun newPointInArea(position: Position, objectIds: List<String>) {
        val draft = areaDraft.value
        if (draft != null &&
            (objectIds.any { it == draft.points.first().id || it == draft.points.last().id })
        ) {
            finishDraftArea()
        } else {
            val newPoint = TrackPoint(generateId(), Point(position))
            areaDraft.value = draft?.copy(points = draft.points + newPoint)
                ?: AreaDraft(
                    points = listOf(newPoint),
                    color = customColor.value,
                    lineThickness = customLineThickness.value,
                    lineStyle = customLineStyle.value,
                )
        }
    }

    private fun finishDraftArea() {
        val draft = areaDraft.value ?: return
        areaDraft.value = null
        if (draft.points.size < 3) return
        val newId = generateId()
        project.addArea(
            Area(
                id = newId,
                points = draft.points,
                color = draft.color,
                lineThickness = draft.lineThickness,
                lineStyle = draft.lineStyle,
            )
        )
        if (!mapToolIsLocked.value) {
            mode.value = Mode.Cursor
            selectedObjectId.value = newId
        }
    }

    private fun addTrackPoint(track: Track, position: Position) {
        project.addTrackPoint(
            track = track,
            index = track.insertIndexFor(position),
            point = TrackPoint(generateId(), Point(position)),
        )
    }

    private fun addAreaPoint(area: Area, position: Position) {
        project.addAreaPoint(
            area = area,
            index = area.insertIndexFor(position),
            point = TrackPoint(generateId(), Point(position)),
        )
    }

    fun removeAreaPoint(areaId: String, pointId: String) {
        val area = document.value.findById(areaId) as? Area ?: return
        removeAreaPointOrArea(area, pointId)
    }

    private fun removeAreaPointOrArea(area: Area, pointId: String) {
        val point = area.points.firstOrNull { it.id == pointId } ?: return
        if (area.points.size > 3) {
            project.removeAreaPoint(area, point)
        } else {
            selectedObjectId.value = null
            project.removeArea(area)
        }
    }

    fun removeTrackPoint(trackId: String, pointId: String) {
        val track = document.value.findById(trackId) as? Track ?: return
        removeTrackPointOrTrack(track, pointId)
    }

    private fun removeTrackPointOrTrack(track: Track, pointId: String) {
        val point = track.points.firstOrNull { it.id == pointId } ?: return
        if (track.points.size > 2) {
            project.removeTrackPoint(track, point)
        } else {
            selectedObjectId.value = null
            project.removeTrack(track)
        }
    }

    fun selectMode(mode: Mode) {
        if (this.mode.value != mode) {
            resetState()
            this.mode.value = mode
        }
    }

    fun selectColor(color: Color) {
        customColor.value = color
        trackDraft.value = trackDraft.value?.copy(color = color)
        areaDraft.value = areaDraft.value?.copy(color = color)

        when (val obj = selectedObject.value) {
            is Pin -> project.colorPin(obj, color)
            is Track -> project.colorTrack(obj, color)
            is Area -> project.colorArea(obj, color)
            is Label -> project.colorLabel(obj, color)
            else -> Unit
        }
    }

    fun setLabelText(text: String) {
        val limitedText = Label.limitText(text)
        val label = selectedObject.value as? Label
        if (label != null) {
            project.setLabelText(label, limitedText)
        } else {
            customLabelText.value = limitedText
        }
    }

    fun selectLineThickness(thickness: Int) {
        customLineThickness.value = thickness
        trackDraft.value = trackDraft.value?.copy(lineThickness = thickness)
        areaDraft.value = areaDraft.value?.copy(lineThickness = thickness)

        when (val obj = selectedObject.value) {
            is Track -> project.setTrackLineThickness(obj, thickness)
            is Area -> project.setAreaLineThickness(obj, thickness)
            else -> Unit
        }
    }

    fun selectLineStyle(style: LineStyle) {
        customLineStyle.value = style
        trackDraft.value = trackDraft.value?.copy(lineStyle = style)
        areaDraft.value = areaDraft.value?.copy(lineStyle = style)

        when (val obj = selectedObject.value) {
            is Track -> project.setTrackLineStyle(obj, style)
            is Area -> project.setAreaLineStyle(obj, style)
            else -> Unit
        }
    }

    fun removeSelectedObject() {
        when (val obj = selectedObject.value) {
            is Pin -> project.removePin(obj)
            is Track -> project.removeTrack(obj)
            is Area -> project.removeArea(obj)
            is Label -> project.removeLabel(obj)
            null -> Unit
        }
        selectedObjectId.value = null
    }

    /** Returns true if the pin can be dragged and the drag session has started. */
    fun startDragPin(pinId: String): Boolean {
        if (mode.value != Mode.Cursor) return false
        val pin = document.value.findById(pinId) as? Pin ?: return false
        selectedObjectId.value = pin.id
        draggingPin.value = PinDrag(pin.id, pin.point)
        return true
    }

    fun updateDragPin(position: Position) {
        val drag = draggingPin.value ?: return
        draggingPin.value = drag.copy(point = Point(position))
    }

    fun finishDragPin() {
        val drag = draggingPin.value ?: return
        draggingPin.value = null
        val pin = document.value.findById(drag.pinId) as? Pin ?: return
        if (pin.point == drag.point) return
        project.movePin(pin, drag.point)
    }

    fun cancelDragPin() {
        draggingPin.value = null
    }

    fun startDragLabel(labelId: String): Boolean {
        if (mode.value != Mode.Cursor) return false
        val label = document.value.findById(labelId) as? Label ?: return false
        selectedObjectId.value = label.id
        draggingLabel.value = LabelDrag(label.id, label.point)
        return true
    }

    fun updateDragLabel(position: Position) {
        val drag = draggingLabel.value ?: return
        draggingLabel.value = drag.copy(point = Point(position))
    }

    fun finishDragLabel() {
        val drag = draggingLabel.value ?: return
        draggingLabel.value = null
        val label = document.value.findById(drag.labelId) as? Label ?: return
        if (label.point == drag.point) return
        project.moveLabel(label, drag.point)
    }

    fun cancelDragLabel() {
        draggingLabel.value = null
    }

    fun startDragTrackPoint(trackId: String, pointId: String): Boolean {
        if (mode.value != Mode.Cursor) return false
        val track = document.value.findById(trackId) as? Track ?: return false
        val point = track.points.firstOrNull { it.id == pointId } ?: return false
        selectedObjectId.value = track.id
        draggingTrackPoint.value = TrackPointDrag(track.id, point.id, point.point)
        return true
    }

    fun updateDragTrackPoint(position: Position) {
        val drag = draggingTrackPoint.value ?: return
        draggingTrackPoint.value = drag.copy(point = Point(position))
    }

    fun finishDragTrackPoint() {
        val drag = draggingTrackPoint.value ?: return
        draggingTrackPoint.value = null
        val track = document.value.findById(drag.trackId) as? Track ?: return
        val point = track.points.firstOrNull { it.id == drag.pointId } ?: return
        project.moveTrackPoint(track, point, drag.point)
    }

    fun cancelDragTrackPoint() {
        draggingTrackPoint.value = null
    }

    fun startDragAreaPoint(areaId: String, pointId: String): Boolean {
        if (mode.value != Mode.Cursor) return false
        val area = document.value.findById(areaId) as? Area ?: return false
        val point = area.points.firstOrNull { it.id == pointId } ?: return false
        selectedObjectId.value = area.id
        draggingAreaPoint.value = AreaPointDrag(area.id, point.id, point.point)
        return true
    }

    fun updateDragAreaPoint(position: Position) {
        val drag = draggingAreaPoint.value ?: return
        draggingAreaPoint.value = drag.copy(point = Point(position))
    }

    fun finishDragAreaPoint() {
        val drag = draggingAreaPoint.value ?: return
        draggingAreaPoint.value = null
        val area = document.value.findById(drag.areaId) as? Area ?: return
        val point = area.points.firstOrNull { it.id == drag.pointId } ?: return
        project.moveAreaPoint(area, point, drag.point)
    }

    fun cancelDragAreaPoint() {
        draggingAreaPoint.value = null
    }

    fun switchLock() {
        mapToolIsLocked.value = !mapToolIsLocked.value
    }

    fun onKeyClicked(event: KeyEvent): Boolean {
        if (event.type != KeyEventType.KeyUp) return false
        when (event.key) {
            Key.Q -> switchLock()
            Key.One -> selectMode(Mode.Cursor)
            Key.Two -> selectMode(Mode.Pin)
            Key.Three -> selectMode(Mode.Track)
            Key.Four -> selectMode(Mode.Area)
            Key.Five -> selectMode(Mode.Text)
            Key.Escape -> resetState()
            Key.Z -> {
                if (event.isCtrlPressed || event.isMetaPressed) {
                    if (event.isShiftPressed) redo() else undo()
                } else {
                    return false
                }
            }


            else -> {
                return false
            }
        }
        return true
    }

    private fun resetState() {
        if (draggingPin.value != null) cancelDragPin()
        if (draggingLabel.value != null) cancelDragLabel()
        if (draggingTrackPoint.value != null) cancelDragTrackPoint()
        if (draggingAreaPoint.value != null) cancelDragAreaPoint()
        if (trackDraft.value != null) trackDraft.value = null
        if (areaDraft.value != null) areaDraft.value = null
        if (selectedObjectId.value != null) selectedObjectId.value = null
        customLabelText.value = ""
    }
}